import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  ShieldCheck, 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  Smartphone, 
  ArrowLeft, 
  KeyRound, 
  Fingerprint, 
  AlertCircle,
  Sparkles
} from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [biometricFeedback, setBiometricFeedback] = useState('');
  const { login, verify2FALogin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBiometricFeedback('');
    setIsLoading(true);
    try {
      if (requires2FA) {
        await verify2FALogin(tempToken, twoFactorCode.trim(), email, password);
      } else {
        const result = await login(email, password);
        if (result?.requires2FA) {
          setRequires2FA(true);
          setTempToken(result.tempToken);
          setTwoFactorCode('');
        }
      }
    } catch (err) {
      const rawMsg = err.response?.data?.message || err.message || 'Failed to authenticate';
      if (typeof rawMsg === 'string' && (rawMsg.toLowerCase().includes('throttler') || rawMsg.toLowerCase().includes('too many requests'))) {
        setError('Too many login attempts. Rate limit refreshed. Please try again.');
      } else {
        setError(rawMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPassword = () => {
    setRequires2FA(false);
    setTempToken('');
    setTwoFactorCode('');
    setError('');
  };

  const handleBiometricPrompt = () => {
    setBiometricFeedback('Enter your master password to establish your local security session.');
    setTimeout(() => setBiometricFeedback(''), 4000);
  };

  return (
    <div className="ios-auth-page">
      {/* Dynamic ambient backdrop blur effect */}
      <div className="ios-auth-ambient-glow" />

      <div className="ios-auth-container animate-fade-in">
        <div className="ios-auth-card">
          
          {/* iOS App Icon Header */}
          <div className="ios-auth-header">
            <div className={`ios-app-icon-tile ${requires2FA ? 'tile-2fa' : ''}`}>
              {requires2FA ? (
                <Smartphone size={26} className="ios-icon-svg" />
              ) : (
                <ShieldCheck size={28} className="ios-icon-svg" />
              )}
            </div>

            {/* <div className="ios-auth-badge">
              <Sparkles size={11} color="#007AFF" />
              <span>Zero-Knowledge Architecture</span>
            </div> */}

            <h1 className="ios-auth-title">
              {requires2FA ? 'Two-Factor Challenge' : 'Welcome Back'}
            </h1>
            <p className="ios-auth-subtitle">
              {requires2FA 
                ? 'Enter the 6-digit code from your authenticator app or recovery key.' 
                : 'Log in to access your secure encrypted vault'}
            </p>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="ios-auth-alert-box animate-shake">
              <AlertCircle size={16} className="ios-alert-icon" />
              <span>{error}</span>
            </div>
          )}

          {/* Biometric Feedback Banner */}
          {biometricFeedback && (
            <div className="ios-auth-info-box animate-fade-in">
              <Fingerprint size={16} />
              <span>{biometricFeedback}</span>
            </div>
          )}

          {/* iOS Form */}
          <form onSubmit={handleSubmit} className="ios-auth-form">
            {!requires2FA ? (
              <>
                {/* Inset Grouped Card */}
                <div className="ios-input-group">
                  {/* Email Field */}
                  <div className="ios-input-row">
                    <div className="ios-input-icon">
                      <Mail size={18} />
                    </div>
                    <div className="ios-input-content">
                      <label className="ios-field-label">Email Address</label>
                      <input
                        type="email"
                        className="ios-native-input"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        inputMode="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="ios-input-divider" />

                  {/* Password Field */}
                  <div className="ios-input-row">
                    <div className="ios-input-icon">
                      <Lock size={18} />
                    </div>
                    <div className="ios-input-content">
                      <label className="ios-field-label">Master Password</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="ios-native-input"
                        placeholder="••••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      className="ios-input-action-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="ios-btn-primary"
                  disabled={isLoading || !email.trim() || !password.trim()}
                >
                  {isLoading ? (
                    <span className="ios-btn-loading">
                      <span className="ios-spinner" />
                      <span>Unlocking Vault...</span>
                    </span>
                  ) : (
                    <>
                      <Lock size={16} />
                      <span>Unlock Vault</span>
                    </>
                  )}
                </button>

                {/* Face ID / Passkey Quick Prompt */}
                <div className="ios-auth-divider-line">
                  <span>or</span>
                </div>

                <button
                  type="button"
                  className="ios-biometric-auth-btn"
                  onClick={handleBiometricPrompt}
                >
                  <div className="ios-biometric-icon-badge">
                    <Fingerprint size={18} />
                  </div>
                  <span className="ios-biometric-btn-text">Sign in with Face ID / Passkey</span>
                </button>
              </>
            ) : (
              <>
                {/* 2FA Inset Group */}
                <div className="ios-input-group">
                  <div className="ios-input-row">
                    <div className="ios-input-icon">
                      <KeyRound size={18} />
                    </div>
                    <div className="ios-input-content">
                      <label className="ios-field-label">6-Digit Authentication Code</label>
                      <input
                        type="text"
                        className="ios-native-input ios-2fa-input"
                        placeholder="000000"
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        maxLength={8}
                        autoFocus
                        required
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="ios-btn-primary"
                  disabled={isLoading || !twoFactorCode.trim()}
                >
                  {isLoading ? (
                    <span className="ios-btn-loading">
                      <span className="ios-spinner" />
                      <span>Verifying Code...</span>
                    </span>
                  ) : (
                    <span>Verify & Unlock</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToPassword}
                  className="ios-link-back-btn"
                >
                  <ArrowLeft size={16} />
                  <span>Back to email and password</span>
                </button>
              </>
            )}
          </form>

          {/* Switch link */}
          {!requires2FA && (
            <div className="ios-auth-footer-nav">
              <span className="ios-nav-text">Don't have an account?</span>
              <Link to="/signup" className="ios-nav-link">
                Create Vault
              </Link>
            </div>
          )}

          {/* Bottom Security Watermark */}
          <div className="ios-auth-security-note">
            <div className="ios-security-badge-row">
              <Shield size={11} className="ios-sec-shield-icon" />
              <span>End-to-End Encrypted</span>
            </div>
            {/* <span className="ios-sec-sub">AES-256-GCM &bull; Zero-Knowledge</span> */}
          </div>

        </div>
      </div>
    </div>
  );
}
